import {
  Button,
  ButtonDropdown,
  ButtonSize,
  Color,
  Divider,
  Flex,
  IconButton,
  IconName,
  NavItem,
  Tooltip,
} from '@billy/design-system'
import { Placement } from '@popperjs/core'
import { ReactNode, useCallback, useState } from 'react'

import * as Styled from './styles'

interface SplitButtonProps<T = string> {
  children?: ReactNode
  dropdownOptions: NavItem<T>[]
  icon?: IconName
  onClick: () => void
  onDropdownItemSelect: (value: T) => void
  size?: ButtonSize
  tooltipLabel?: ReactNode
  tooltipPlacement?: Placement
}

export const SplitButton = <T,>({
  children,
  dropdownOptions,
  icon,
  onClick,
  onDropdownItemSelect,
  size = 'md',
  tooltipLabel,
  tooltipPlacement,
}: SplitButtonProps<T>) => {
  const [isDropdownActive, setIsDropdownActive] = useState(false)

  const handleDropdownItemSelect = useCallback(
    (_: string, value: T) => {
      onDropdownItemSelect(value)
    },
    [onDropdownItemSelect],
  )

  const handleDropdownClose = useCallback(() => {
    setIsDropdownActive(false)
  }, [])

  const handleDropdownOpen = useCallback(() => {
    setIsDropdownActive(true)
  }, [])

  return (
    <Styled.SplitButtonWrapper>
      <Flex alignItems="center">
        <Styled.AddButtonWrapper>
          <Tooltip forceHidden={!tooltipLabel} label={tooltipLabel} placement={tooltipPlacement}>
            <Button icon={icon} onClick={onClick} skipRightRounding variant="text">
              {children}
            </Button>
          </Tooltip>
        </Styled.AddButtonWrapper>

        {dropdownOptions.length && (
          <>
            <Divider direction="vertical" dividerColor={Color.Grey200} />
            <ButtonDropdown
              items={dropdownOptions}
              onClose={handleDropdownClose}
              onOpen={handleDropdownOpen}
              onSelect={handleDropdownItemSelect}
              placement="bottom-end"
              variant="outlined"
              triggerElement={
                <Styled.TriggerButtonWrapper>
                  <IconButton
                    active={isDropdownActive}
                    variant="text"
                    icon={isDropdownActive ? 'chevronUp' : 'chevronDown'}
                    size={size}
                  />
                </Styled.TriggerButtonWrapper>
              }
            />
          </>
        )}
      </Flex>
    </Styled.SplitButtonWrapper>
  )
}
